import React from 'react';

export const legalLastUpdated = 'Jan 04, 2025';
export const legalCompanyName = 'mapleos';
export const legalContactEmail = 'hello@mapleos.ai';
export const legalCompanyAddress = '77 Westmorland Street, Suite 300, Fredericton, New Brunswick, Canada, E3B-6Z3';

export function EulaDocumentContent() {
  return (
    <>
      <p>
        This End User License Agreement ("EULA") is a legal agreement between you ("User") and mapleos.ai ("we,"
        "us," or "our") governing your access to and use of our AI chat services (the "Service") in Canada.
      </p>

      <p>Effective Date: {legalLastUpdated}</p>

      <h2 className="mb-3 mt-6 text-xl font-semibold">1. License Grant</h2>
      <p>
        mapleos.ai grants you a limited, non-exclusive, non-transferable, revocable license to access and use the
        Service for personal or business purposes, in accordance with this EULA.
      </p>

      <h2 className="mb-3 mt-6 text-xl font-semibold">2. Restrictions</h2>
      <p>You may not:</p>
      <ul className="mb-4 ml-6 list-disc">
        <li>Modify, reverse engineer, or decompile the Service.</li>
        <li>Use the Service to infringe on intellectual property rights or violate any applicable laws.</li>
        <li>Use the Service for any illegal, abusive, or harmful purposes.</li>
      </ul>

      <h2 className="mb-3 mt-6 text-xl font-semibold">3. User Data and Privacy</h2>

      <h3 className="mb-2 mt-4 text-lg font-semibold">3.1 Data Storage and Security</h3>
      <p>
        We are committed to safeguarding your personal information. All user data collected through the Service is
        securely stored and processed within Canada in compliance with applicable Canadian data privacy laws, including
        PIPEDA.
      </p>

      <h3 className="mb-2 mt-4 text-lg font-semibold">3.2 Data Usage</h3>
      <p>
        We use your data to improve our Service and provide personalized experiences. For more details, please review
        our <a href="/data-policy">Data Policy</a>.
      </p>

      <h3 className="mb-2 mt-4 text-lg font-semibold">3.3 Third-Party Access</h3>
      <p>We do not share your personal data with third parties unless required by law or with your explicit consent.</p>

      <h2 className="mb-3 mt-6 text-xl font-semibold">4. Intellectual Property</h2>
      <p>
        All intellectual property rights in the Service, including but not limited to its content, software, and
        trademarks, are owned by mapleos.ai or its licensors. This EULA does not grant you any ownership rights.
      </p>

      <h2 className="mb-3 mt-6 text-xl font-semibold">5. Termination</h2>
      <p>We reserve the right to suspend or terminate your access to the Service if you violate any terms of this EULA.</p>

      <h2 className="mb-3 mt-6 text-xl font-semibold">6. Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by law, mapleos.ai is not liable for any indirect, incidental, or consequential
        damages arising from your use of the Service.
      </p>

      <h2 className="mb-3 mt-6 text-xl font-semibold">7. Governing Law</h2>
      <p>
        This EULA shall be governed by and construed in accordance with the laws of the Province of New Brunswick,
        Canada, without regard to its conflict of law principles.
      </p>

      <h2 className="mb-3 mt-6 text-xl font-semibold">8. Amendments</h2>
      <p>
        We may update this EULA from time to time. Any changes will be posted on our website, and your continued use
        of the Service constitutes acceptance of the updated terms.
      </p>

      <h2 className="mb-3 mt-6 text-xl font-semibold">9. Contact Information</h2>
      <p>If you have any questions about this Agreement, please contact us at:</p>
      <p>
        <strong>{legalCompanyName}</strong>
      </p>
      <p>{legalCompanyAddress}</p>
      <p>
        <a href={`mailto:${legalContactEmail}`}>{legalContactEmail}</a>
      </p>
    </>
  );
}

export function DataPolicyDocumentContent() {
  return (
    <>
      <p>
        <strong>Effective Date:</strong> {legalLastUpdated}
      </p>

      <h2 className="mb-3 mt-6 text-xl font-semibold">1. Data Sovereignty and Residency</h2>
      <p>
        1.1 All confidential and identifying data processed by mapleos.ai is stored exclusively in Canada. Our data
        centers are located in Eastern Canada, specifically in Ontario, Quebec, and New Brunswick.
      </p>
      <p>
        1.2 Embeddings and other proprietary data structures generated by mapleos.ai are hosted solely within Canadian
        facilities to ensure compliance with Canadian privacy laws and regulations.
      </p>
      <p>
        1.3 mapleos.ai does not transmit any identifying data to services or entities located outside of Canada,
        including the United States.
      </p>

      <h2 className="mb-3 mt-6 text-xl font-semibold">2. Patriot Act and U.S. Data Compliance</h2>
      <p>
        2.1 mapleos.ai acknowledges the implications of the United States Patriot Act and ensures that no data processed
        by our services is stored on or transmitted to U.S.-based infrastructure or service providers.
      </p>
      <p>
        2.2 We have implemented robust controls to prevent unauthorized access by any foreign entity or government. Our
        systems prioritize user and enterprise privacy within Canadian legal frameworks.
      </p>
      <p>
        2.3 While we allow users to access Large Language Models (LLMs) hosted outside of Canada, they are clearly
        marked with a country flag. Users concerned about jurisdictional hosting should use our Canadian-hosted models.
        We do not store embeddings on servers outside of Canada.
      </p>

      <h2 className="mb-3 mt-6 text-xl font-semibold">3. Enterprise-Level Security</h2>
      <p>
        3.1 For enterprise clients, mapleos.ai provides secure access to Canadian-hosted LLMs, ensuring data remains
        within Canadian borders.
      </p>
      <p>3.2 We offer dedicated infrastructure and private hosting options tailored to specific organizational needs.</p>
      <p>
        3.3 All enterprise data transfers are encrypted using industry-standard protocols to prevent unauthorized access
        or interception.
      </p>

      <h2 className="mb-3 mt-6 text-xl font-semibold">4. Compliance with International Data Policies</h2>
      <p>
        4.1 mapleos.ai adheres to the General Data Protection Regulation (GDPR) for EU-based data, ensuring
        transparency, accountability, and consent.
      </p>
      <p>4.2 We comply with other international frameworks including:</p>
      <ul className="mb-4 ml-6 list-disc">
        <li>PIPEDA (Canada)</li>
        <li>CCPA (California), where applicable</li>
        <li>UK Data Protection Act</li>
      </ul>
      <p>4.3 Users and enterprise clients will be notified of any changes to our data processing practices.</p>

      <h2 className="mb-3 mt-6 text-xl font-semibold">5. Data Retention and Minimization</h2>
      <p>
        5.1 mapleos.ai retains data only as long as necessary to fulfill the intended purpose or to comply with legal
        obligations.
      </p>
      <p>5.2 We apply data minimization principles, collecting and processing only essential information.</p>

      <h2 className="mb-3 mt-6 text-xl font-semibold">6. User Rights</h2>
      <p>
        6.1 Users have the right to access, correct, or delete their data stored by mapleos.ai. Requests can be
        submitted through our support portal or via contact below.
      </p>
      <p>6.2 Enterprise clients may define specific data access protocols in their service agreements.</p>

      <h2 className="mb-3 mt-6 text-xl font-semibold">7. Security Measures</h2>
      <p>7.1 mapleos.ai uses encryption, firewalls, and intrusion detection systems to safeguard data.</p>
      <p>7.2 We perform regular audits and assessments to ensure compliance with evolving standards.</p>
      <p>
        7.3 Employees and contractors with data access are bound by confidentiality and trained regularly on data
        protection.
      </p>

      <h2 className="mb-3 mt-6 text-xl font-semibold">8. Transparency and Accountability</h2>
      <p>
        8.1 mapleos.ai is committed to transparent data practices and regularly updates our policies to reflect
        international standards.
      </p>
      <p>
        8.2 In the event of a data breach, affected users and clients will be notified promptly, and remediation steps
        will be taken immediately.
      </p>

      <h2 className="mb-3 mt-6 text-xl font-semibold">9. Contact Information</h2>
      <p>If you have any questions or requests regarding this Data Policy, please contact us:</p>
      <p>
        <strong>mapleos.ai</strong>
      </p>
      <p>{legalCompanyAddress}</p>
      <p>
        <a href={`mailto:${legalContactEmail}`}>{legalContactEmail}</a>
      </p>

      <p className="text-sm">
        By using mapleos.ai services, you agree to this Data Policy. We recommend reviewing it periodically for
        updates.
      </p>
    </>
  );
}
